import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Form,
  Button,
  Row,
  Col,
  Spinner,
  Alert,
} from "react-bootstrap";
import moment from "moment";
import axiosClient from "../api/axiosClient";
import { API_BASE_URL } from "../config/api";
import { Bar } from "react-chartjs-2";

const UserJourney = () => {
  const [journeyData, setJourneyData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [startDate, setStartDate] = useState(
    moment().subtract(30, "days").format("YYYY-MM-DD"),
  );
  const [endDate, setEndDate] = useState(moment().format("YYYY-MM-DD"));
  const [forceFilter, setForceFilter] = useState(""); // "", "true", "false"

  // Fetch journey data for the date range
  const fetchJourneyData = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await axiosClient.get(
        "https://api.viralgpt.com/admin/clickthrough-journey",
        {
          params: {
            startDate,
            endDate,
            forceFilter,
          },
        },
      );

      // Ensure each day has a dropoffs object
      const processedData = response.data.map((day) => {
        return {
          ...day,
          dropoffs: day.dropoffs || {},
        };
      });

      setJourneyData(processedData);
    } catch (error) {
      console.error("Error fetching user journey data:", error);
      setError("Failed to load user journey data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Initial data fetch
  useEffect(() => {
    fetchJourneyData();
  }, [startDate, endDate, forceFilter]);

  // Helper function to check if a date is today
  const isToday = (dateString) => {
    return moment(dateString).isSame(moment(), "day");
  };

  // Format numbers with commas
  const formatNumber = (value) => {
    return new Intl.NumberFormat("en-US").format(value || 0);
  };

  // Format percentage values
  const formatPercentage = (value) => {
    return `${value}%`;
  };

  // Calculate totals for the footer
  const calculateTotals = () => {
    if (!journeyData.length) return { dropoffs: {} };

    const totals = journeyData.reduce(
      (acc, day) => {
        return {
          home_view: (acc.home_view || 0) + (parseInt(day.home_view) || 0),
          AddToCart: (acc.AddToCart || 0) + (parseInt(day.AddToCart) || 0),
          CompleteRegistration:
            (acc.CompleteRegistration || 0) +
            (parseInt(day.CompleteRegistration) || 0),
          InitiateCheckout:
            (acc.InitiateCheckout || 0) + (parseInt(day.InitiateCheckout) || 0),
          Purchase: (acc.Purchase || 0) + (parseInt(day.Purchase) || 0),
          dropoffs: acc.dropoffs || {},
        };
      },
      { dropoffs: {} },
    );

    // Define the funnel stages based on forced/non-forced journey
    let stages;
    if (forceFilter === "true") {
      // Forced journey: Add to Cart → Register → Initiate Checkout → Purchase
      stages = [
        "AddToCart",
        "CompleteRegistration",
        "InitiateCheckout",
        "Purchase",
      ];
    } else if (forceFilter === "false") {
      // Non-forced journey: Register → Initiate Checkout → Purchase
      stages = ["CompleteRegistration", "InitiateCheckout", "Purchase"];
    } else {
      // Default journey: Home Page Views → Add to Cart → Register → Initiate Checkout → Purchase
      stages = [
        "home_view",
        "AddToCart",
        "CompleteRegistration",
        "InitiateCheckout",
        "Purchase",
      ];
    }

    // Calculate drop-offs between stages for totals
    for (let i = 0; i < stages.length - 1; i++) {
      const currentStage = stages[i];
      const nextStage = stages[i + 1];

      const currentCount = totals[currentStage] || 0;
      const nextCount = totals[nextStage] || 0;

      if (currentCount > 0) {
        const dropoffRate = ((currentCount - nextCount) / currentCount) * 100;
        totals.dropoffs[`${currentStage}_to_${nextStage}`] = {
          absolute: currentCount - nextCount,
          percentage: parseFloat(dropoffRate.toFixed(2)),
        };
      } else {
        totals.dropoffs[`${currentStage}_to_${nextStage}`] = {
          absolute: 0,
          percentage: 0,
        };
      }
    }

    return totals;
  };

  const totals = calculateTotals();

  // Prepare chart data for funnel visualization
  const prepareFunnelChartData = () => {
    // Define the funnel stages based on forced/non-forced journey
    let stages, labels;

    if (forceFilter === "true") {
      // Forced journey: Add to Cart → Register → Initiate Checkout → Purchase
      stages = [
        "AddToCart",
        "CompleteRegistration",
        "InitiateCheckout",
        "Purchase",
      ];
      labels = [
        "Add to Cart",
        "Registrations",
        "Initiate Checkout",
        "Purchase",
      ];
    } else if (forceFilter === "false") {
      // Non-forced journey: Register → Initiate Checkout → Purchase
      stages = ["CompleteRegistration", "InitiateCheckout", "Purchase"];
      labels = ["Registrations", "Initiate Checkout", "Purchase"];
    } else {
      // Default journey: Home Page Views → Add to Cart → Register → Initiate Checkout → Purchase
      stages = [
        "home_view",
        "AddToCart",
        "CompleteRegistration",
        "InitiateCheckout",
        "Purchase",
      ];
      labels = [
        "Home Page Views",
        "Add to Cart",
        "Registrations",
        "Initiate Checkout",
        "Purchase",
      ];
    }

    return {
      labels: labels,
      datasets: [
        {
          label: "Conversion Funnel",
          data: stages.map((stage) => totals[stage] || 0),
          backgroundColor: [
            "rgba(54, 162, 235, 0.6)",
            "rgba(75, 192, 192, 0.6)",
            "rgba(153, 102, 255, 0.6)",
            "rgba(255, 159, 64, 0.6)",
            "rgba(255, 99, 132, 0.6)",
          ],
          borderColor: [
            "rgba(54, 162, 235, 1)",
            "rgba(75, 192, 192, 1)",
            "rgba(153, 102, 255, 1)",
            "rgba(255, 159, 64, 1)",
            "rgba(255, 99, 132, 1)",
          ],
          borderWidth: 1,
        },
      ],
    };
  };

  // Prepare chart data for daily conversions
  const prepareDailyConversionData = () => {
    // Get the last 7 days of data
    const last7Days = journeyData
      .sort((a, b) => moment(b.date).valueOf() - moment(a.date).valueOf())
      .slice(0, 7)
      .reverse();

    // Define the funnel stages based on forced/non-forced journey
    let stages, stageLabels;

    if (forceFilter === "true") {
      stages = [
        "AddToCart",
        "CompleteRegistration",
        "InitiateCheckout",
        "Purchase",
      ];
      stageLabels = ["Add to Cart", "Registrations", "Checkout", "Purchase"];
    } else if (forceFilter === "false") {
      stages = ["CompleteRegistration", "InitiateCheckout", "Purchase"];
      stageLabels = ["Registrations", "Checkout", "Purchase"];
    } else {
      stages = [
        "home_view",
        "AddToCart",
        "CompleteRegistration",
        "InitiateCheckout",
        "Purchase",
      ];
      stageLabels = [
        "Home Views",
        "Add to Cart",
        "Registrations",
        "Checkout",
        "Purchase",
      ];
    }

    return {
      labels: last7Days.map((day) => moment(day.date).format("MMM DD")),
      datasets: stages.map((stage, index) => ({
        label: stageLabels[index],
        data: last7Days.map((day) => day[stage] || 0),
        backgroundColor: [
          "rgba(54, 162, 235, 0.6)",
          "rgba(75, 192, 192, 0.6)",
          "rgba(153, 102, 255, 0.6)",
          "rgba(255, 159, 64, 0.6)",
          "rgba(255, 99, 132, 0.6)",
        ][index % 5],
        borderColor: [
          "rgba(54, 162, 235, 1)",
          "rgba(75, 192, 192, 1)",
          "rgba(153, 102, 255, 1)",
          "rgba(255, 159, 64, 1)",
          "rgba(255, 99, 132, 1)",
        ][index % 5],
        borderWidth: 1,
      })),
    };
  };

  // Prepare chart data for dropoff rates
  const prepareDropoffRateData = () => {
    // Define the funnel stages based on forced/non-forced journey
    let stages, dropoffLabels;

    if (forceFilter === "true") {
      stages = [
        "AddToCart",
        "CompleteRegistration",
        "InitiateCheckout",
        "Purchase",
      ];
      dropoffLabels = [
        "Cart to Register",
        "Register to Checkout",
        "Checkout to Purchase",
      ];
    } else if (forceFilter === "false") {
      stages = ["CompleteRegistration", "InitiateCheckout", "Purchase"];
      dropoffLabels = ["Register to Checkout", "Checkout to Purchase"];
    } else {
      stages = [
        "home_view",
        "AddToCart",
        "CompleteRegistration",
        "InitiateCheckout",
        "Purchase",
      ];
      dropoffLabels = [
        "Home to Cart",
        "Cart to Register",
        "Register to Checkout",
        "Checkout to Purchase",
      ];
    }

    // Calculate average dropoff rates
    const dropoffRates = [];
    for (let i = 0; i < stages.length - 1; i++) {
      const currentStage = stages[i];
      const nextStage = stages[i + 1];
      const dropoffKey = `${currentStage}_to_${nextStage}`;

      // Calculate average dropoff rate from all days with data
      let totalRate = 0;
      let daysWithData = 0;

      journeyData.forEach((day) => {
        if (day.dropoffs && day.dropoffs[dropoffKey] && day[currentStage] > 0) {
          totalRate += day.dropoffs[dropoffKey].percentage;
          daysWithData++;
        }
      });

      const avgRate = daysWithData > 0 ? totalRate / daysWithData : 0;
      dropoffRates.push(avgRate);
    }

    return {
      labels: dropoffLabels,
      datasets: [
        {
          label: "Average Dropoff Rate (%)",
          data: dropoffRates,
          backgroundColor: "rgba(255, 99, 132, 0.6)",
          borderColor: "rgba(255, 99, 132, 1)",
          borderWidth: 1,
        },
      ],
    };
  };

  // Prepare chart data for conversion rate over time
  const prepareConversionRateTimeData = () => {
    // Get the last 14 days of data
    const last14Days = journeyData
      .sort((a, b) => moment(a.date).valueOf() - moment(b.date).valueOf())
      .slice(-14);

    // Define the funnel stages based on forced/non-forced journey
    let stages;

    if (forceFilter === "true") {
      stages = ["AddToCart", "Purchase"];
    } else if (forceFilter === "false") {
      stages = ["CompleteRegistration", "Purchase"];
    } else {
      stages = ["home_view", "Purchase"];
    }

    // Calculate daily conversion rates
    const conversionRates = last14Days.map((day) => {
      const firstStage = day[stages[0]] || 0;
      const lastStage = day[stages[1]] || 0;
      return firstStage > 0 ? (lastStage / firstStage) * 100 : 0;
    });

    return {
      labels: last14Days.map((day) => moment(day.date).format("MMM DD")),
      datasets: [
        {
          label: "Overall Conversion Rate (%)",
          data: conversionRates,
          backgroundColor: "rgba(75, 192, 192, 0.6)",
          borderColor: "rgba(75, 192, 192, 1)",
          borderWidth: 1,
          fill: false,
          tension: 0.1,
        },
      ],
    };
  };

  // Chart options
  const funnelChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: true,
        text: "Conversion Funnel Totals",
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Views",
        },
      },
    },
  };

  const dailyConversionOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: true,
        text: "Daily Conversions (Last 7 Days)",
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Views",
        },
      },
    },
  };

  const dropoffRateOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: true,
        text: "Average Dropoff Rates",
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        title: {
          display: true,
          text: "Dropoff Rate (%)",
        },
      },
    },
  };

  const conversionRateTimeOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: true,
        text: "Overall Conversion Rate Over Time",
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        title: {
          display: true,
          text: "Conversion Rate (%)",
        },
      },
    },
  };

  // Render the appropriate table based on the filter
  const renderJourneyTable = () => {
    // Define the funnel stages and headers based on forced/non-forced journey
    let stages, headers;

    if (forceFilter === "true") {
      // Forced journey: Add to Cart → Register → Initiate Checkout → Purchase
      stages = [
        "AddToCart",
        "CompleteRegistration",
        "InitiateCheckout",
        "Purchase",
      ];
      headers = [
        "Date",
        "Add to Cart",
        "Drop-off %",
        "Registrations",
        "Drop-off %",
        "Initiate Checkout",
        "Drop-off %",
        "Purchase",
        "Overall Conversion %",
      ];
    } else if (forceFilter === "false") {
      // Non-forced journey: Register → Initiate Checkout → Purchase
      stages = ["CompleteRegistration", "InitiateCheckout", "Purchase"];
      headers = [
        "Date",
        "Registrations",
        "Drop-off %",
        "Initiate Checkout",
        "Drop-off %",
        "Purchase",
        "Overall Conversion %",
      ];
    } else {
      // Default journey: Home Page Views → Add to Cart → Register → Initiate Checkout → Purchase
      stages = [
        "home_view",
        "AddToCart",
        "CompleteRegistration",
        "InitiateCheckout",
        "Purchase",
      ];
      headers = [
        "Date",
        "Home Page Views",
        "Drop-off %",
        "Add to Cart",
        "Drop-off %",
        "Registrations",
        "Drop-off %",
        "Initiate Checkout",
        "Drop-off %",
        "Purchase",
        "Overall Conversion %",
      ];
    }

    return (
      <Table striped bordered hover className="journey-table">
        <thead className="bg-light">
          <tr>
            {headers.map((header, index) => (
              <th key={index}>{header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {journeyData
            .sort((a, b) => moment(b.date).valueOf() - moment(a.date).valueOf()) // Sort by date descending
            .map((day) => (
              <tr
                key={day.date}
                className={isToday(day.date) ? "today-highlight" : ""}
              >
                <td>
                  {isToday(day.date) ? (
                    <span className="today-label">
                      {moment(day.date).format("MMM DD, YYYY")}
                    </span>
                  ) : (
                    moment(day.date).format("MMM DD, YYYY")
                  )}
                </td>

                {/* Rest of the row rendering remains the same */}
                {stages.map((stage, index) => (
                  <React.Fragment key={stage}>
                    <td>{formatNumber(day[stage] || 0)}</td>

                    {index < stages.length - 1 && (
                      <td className="text-danger">
                        {formatPercentage(
                          day.dropoffs?.[`${stage}_to_${stages[index + 1]}`]
                            ?.percentage || 0,
                        )}
                      </td>
                    )}
                  </React.Fragment>
                ))}

                <td
                  className={
                    day[stages[0]] > 0 &&
                    day[stages[stages.length - 1]] / day[stages[0]] > 0.1
                      ? "text-success"
                      : "text-danger"
                  }
                >
                  {day[stages[0]] > 0
                    ? formatPercentage(
                        (
                          (day[stages[stages.length - 1]] / day[stages[0]]) *
                          100
                        ).toFixed(2),
                      )
                    : "0%"}
                </td>
              </tr>
            ))}
        </tbody>
        <tfoot className="table-dark">
          <tr>
            <td>
              <strong>TOTALS</strong>
            </td>

            {/* Render cells for each stage and drop-off in totals */}
            {stages.map((stage, index) => (
              <React.Fragment key={stage}>
                <td>{formatNumber(totals[stage] || 0)}</td>

                {/* Add empty cell instead of drop-off percentage */}
                {index < stages.length - 1 && <td>-</td>}
              </React.Fragment>
            ))}

            {/* Overall conversion rate for totals */}
            <td>-</td>
          </tr>
        </tfoot>
      </Table>
    );
  };

  return (
    <Card className="mb-4">
      <Card.Body className="p-0">
        <Card.Title className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center mb-4 p-3">
          <span>User Clickthrough Journey</span>
          <div className="d-flex gap-2 mt-2 mt-sm-0 w-100 w-sm-auto">
            <Form.Group as={Row} className="align-items-center w-100">
              <Form.Label column sm="auto">
                Date Range:
              </Form.Label>
              <Col sm="auto">
                <Form.Control
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </Col>
              <Col sm="auto">to</Col>
              <Col sm="auto">
                <Form.Control
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </Col>
              <Col sm="auto">
                <Form.Select
                  value={forceFilter}
                  onChange={(e) => setForceFilter(e.target.value)}
                >
                  <option value="">All Users</option>
                  <option value="true">Forced Affiliate</option>
                  <option value="false">Non-Forced</option>
                </Form.Select>
              </Col>
              <Col sm="auto">
                <Button variant="outline-primary" onClick={fetchJourneyData}>
                  Refresh
                </Button>
              </Col>
            </Form.Group>
          </div>
        </Card.Title>

        {error && <Alert variant="danger">{error}</Alert>}

        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        ) : (
          <>
            <div className="table-responsive">{renderJourneyTable()}</div>

            {/* Charts Section */}
            <div className="p-3">
              <h4 className="mb-4">Journey Analytics</h4>

              <Row className="mb-4">
                <Col md={6}>
                  <Card>
                    <Card.Body>
                      <div style={{ height: "400px" }}>
                        <Bar
                          data={prepareFunnelChartData()}
                          options={funnelChartOptions}
                        />
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6}>
                  <Card>
                    <Card.Body>
                      <div style={{ height: "400px" }}>
                        <Bar
                          data={prepareDropoffRateData()}
                          options={dropoffRateOptions}
                        />
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <Row className="mb-4">
                <Col md={6}>
                  <Card>
                    <Card.Body>
                      <div style={{ height: "400px" }}>
                        <Bar
                          data={prepareDailyConversionData()}
                          options={dailyConversionOptions}
                        />
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6}>
                  <Card>
                    <Card.Body>
                      <div style={{ height: "400px" }}>
                        <Bar
                          data={prepareConversionRateTimeData()}
                          options={conversionRateTimeOptions}
                        />
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </div>

            <style jsx="true">{`
              .today-label {
                font-weight: bold;
                color: #daa520;
              }
              .today-highlight {
                background-color: rgba(218, 165, 32, 0.1);
              }

              /* Default (non-mobile) table styling */
              .journey-table {
                font-size: 0.9rem;
              }

              /* Mobile adjustments */
              @media (max-width: 576px) {
                .journey-table {
                  table-layout: fixed;
                  width: 100%;
                  font-size: 0.4rem;
                }
                .journey-table th,
                .journey-table td {
                  padding: 0;
                  text-align: center;
                  white-space: nowrap;
                  overflow: hidden;
                  text-overflow: clip;
                }
              }
            `}</style>
          </>
        )}
      </Card.Body>
    </Card>
  );
};

export default UserJourney;
